import { isEqual } from "es-toolkit";
import { isEmpty } from "es-toolkit/compat";
import type { Getter, Setter } from "jotai";
import { atomEffect } from "jotai-effect";
import { loadable } from "jotai/utils";
import { normalize } from "pathe";
import YAML from "yaml";

import { HttpRequestPageSchema, resolveFilePathToPage } from "@moment/api-collab";
import { DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { $isCloning } from "../clone-document/clone-state.atom";
import {
	$document,
	$documentLocalPath,
	docsListCollabEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { loadedKeys } from "../document-loader/loader.atom";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import {
	$applyExternalDocumentMetadata,
	$documentMetadata,
} from "../document-metadata/metadata-state.atom";
import {
	PRIMARY_AGENTS_MD_PAGE_ID,
	isPrimaryAgentInstructionPage,
} from "../document/agents-md.atom";
import { $gitStatus } from "../git/git-repository.atom";
import {
	$unloadCanvasSnapshot,
	canvasSnapshotKeyForDocsRoute,
} from "../page/page-canvas-state.atom";
import { $isRenamingPage } from "../page/rename-page-dir.atom";
import { $unloadEditorState } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import {
	$applyExternalHttpRequestPage,
	$httpRequestPage,
	$unloadHttpRequestPage,
} from "../request-page/request-page.atom";
import { requestPageKeyForDocsRoute } from "../request-page/requestPageKey";
import { $applyExternalChange } from "./apply-external-change.atom";
import { $fileChanges } from "./file-watcher.atom";
import { $syncNewPagesToCollab } from "./sync-new-pages-to-collab.atom";

const logger = new Logger("file-watcher-effect");

const DOCUMENT_ID_REGEX = /\/documents\/([^/]+)\//;

const isMomentYamlPath = (path: string) => {
	const normalized = normalize(path);
	// Must be at document root: /documents/<docId>/moment.yml
	// Not nested like /documents/<docId>/pages/<pageId>/moment.yml
	return /\/documents\/[^/]+\/moment\.ya?ml$/.test(normalized);
};

const isYamlPath = (path: string) => path.endsWith(".yml") || path.endsWith(".yaml");

const getDocumentIdFromPath = (path: string) => {
	// NOTE: We *SHOULD* be using `parseDocumentIdFromPath` here, but `atomEffect` can't be `async`.
	// Arg.
	const match = normalize(path).match(DOCUMENT_ID_REGEX);
	return match?.[1] ?? null;
};

const normalizePageType = (type: string | undefined) => type ?? "markdown";

const reconcileMetadataPages = (
	documentId: string,
	previousPages: Array<{ id: string; type?: string; path?: string }>,
	nextPages: Array<{ id: string; type?: string; path?: string }>,
	set: Setter
) => {
	const previousById = new Map(previousPages.map((page) => [page.id, page]));
	const nextById = new Map(nextPages.map((page) => [page.id, page]));

	for (const [pageId, previous] of previousById) {
		const next = nextById.get(pageId);
		const previousType = normalizePageType(previous.type);
		const nextType = next ? normalizePageType(next.type) : undefined;
		const changed = !next || previousType !== nextType || previous.path !== next.path;
		if (!changed) {
			continue;
		}

		if (previousType === "request") {
			const requestKey = requestPageKeyForDocsRoute({ documentId, pageId });
			loadedKeys.delete(requestKey);
			set($unloadHttpRequestPage, requestKey);
		} else if (previousType === "markdown") {
			const editorKey = editorStateKeyForDocsRoute({ documentId, pageId });
			loadedKeys.delete(editorKey);
			set($unloadEditorState, editorKey);
		} else if (previousType === "canvas") {
			const canvasKey = canvasSnapshotKeyForDocsRoute({ documentId, pageId });
			loadedKeys.delete(canvasKey);
			set($unloadCanvasSnapshot, canvasKey);
		}
	}
};

const handleMarkdownChange = (path: string, content: string, get: Getter, set: Setter): boolean => {
	const documentId = getDocumentIdFromPath(path);
	if (!documentId) {
		logger.debug("Could not extract documentId from path", { path });
		return true;
	}

	// Skip file changes while a clone is in progress for this document.
	// The clone writes files to disk which triggers the file watcher, but the
	// document will be loaded fresh from disk after the clone completes.
	// Applying these intermediate changes would incorrectly show them as suggestions.
	if (get($isCloning(documentId))) {
		logger.debug("Document is being cloned, dropping file change", { documentId });
		return true;
	}

	// Skip file changes while a page directory rename is in progress.
	// The rename moves files which triggers the file watcher, but metadata
	// will be updated after the rename completes.
	if (get($isRenamingPage(documentId))) {
		logger.debug("Page directory rename in progress, dropping file change", { documentId });
		return true;
	}

	const localPathLoadable = get(loadable($documentLocalPath(documentId)));
	if (localPathLoadable.state === "loading") {
		logger.debug("Document local path loading", { documentId });
		return false;
	}
	if (localPathLoadable.state === "hasError") {
		logger.debug("Document local path error, will retry", {
			documentId,
			error: localPathLoadable.error,
		});
		return false;
	}
	if (!localPathLoadable.data) {
		logger.debug("Document local path undefined, will retry", { documentId });
		return false;
	}
	const documentRootPath = localPathLoadable.data;

	const metadataKey = metadataKeyForDocsRoute(documentId);
	const metadata = get($documentMetadata(metadataKey));
	if (!metadata) {
		logger.debug("Document metadata not loaded, will retry", { documentId });
		return false;
	}

	const resolved = resolveFilePathToPage(
		path,
		documentId,
		documentRootPath,
		metadata.pagesMetadata
	);

	let pageId: string;
	if (resolved) {
		pageId = resolved.pageId;
	} else {
		// resolveFilePathToPage only checks pagesMetadata. Root-level AGENTS.md
		// is a standalone page outside pagesMetadata — resolve it by filename.
		const normalizedRoot = normalize(documentRootPath).endsWith("/")
			? normalize(documentRootPath)
			: normalize(documentRootPath) + "/";
		const relativePath = normalize(path).startsWith(normalizedRoot)
			? normalize(path).slice(normalizedRoot.length)
			: undefined;

		if (relativePath && isPrimaryAgentInstructionPage(relativePath)) {
			pageId = PRIMARY_AGENTS_MD_PAGE_ID;
		} else {
			logger.debug("Could not resolve file path to page, dropping", {
				path,
				documentRootPath,
			});
			return true;
		}
	}

	const pageMetadata = metadata.pagesMetadata.find((page) => page.id === pageId);
	if (pageMetadata && normalizePageType(pageMetadata.type) !== "markdown") {
		logger.debug("Resolved page is not a markdown page", {
			documentId,
			pageId,
			type: pageMetadata.type,
		});
		return true;
	}

	// When the document is a collab document on collab/main, skip wrapping disk changes
	// in suggestion marks. The only reason disk loading is active for such a document is
	// because of conflict resolution, so these changes are deliberate resolutions.
	let skipSuggestions = false;
	const document = get($document(documentId));
	if (document) {
		const remoteEntry = docsListItemRemoteEntry(document);
		const collabEntry = docsListCollabEntry(remoteEntry);
		const hasCollab = !!collabEntry && !isEmpty(collabEntry);
		const gitStatusLoadable = get(loadable($gitStatus(documentId)));
		const isOnCollabBranch =
			gitStatusLoadable.state === "hasData" &&
			gitStatusLoadable.data?.branch === "collab/main";
		skipSuggestions = hasCollab && isOnCollabBranch;
	}

	// The managed section (if any) is wrapped in <section readonly> and handled
	// as a readonly_section ProseMirror node. No stripping needed — the full
	// content is passed through and the readonly_section round-trips through
	// ProseMirror serialization.

	logger.debug("Calling $applyExternalChange", {
		documentId,
		pageId,
		contentLength: content.length,
		skipSuggestions,
	});
	set($applyExternalChange, { documentId, pageId, content, skipSuggestions });

	return true;
};

const handleMomentYamlChange = (
	path: string,
	content: string,
	get: Getter,
	set: Setter
): boolean => {
	const documentId = getDocumentIdFromPath(path);
	if (!documentId) {
		logger.debug("Could not extract documentId from path", { path });
		return true;
	}

	if (get($isCloning(documentId))) {
		logger.debug("Document is being cloned, dropping metadata change", { documentId });
		return true;
	}

	if (get($isRenamingPage(documentId))) {
		logger.debug("Page directory rename in progress, dropping metadata change", { documentId });
		return true;
	}

	const metadataKey = metadataKeyForDocsRoute(documentId);
	const currentMetadata = get($documentMetadata(metadataKey));
	if (!currentMetadata) {
		logger.debug("Document metadata not loaded, will retry", { documentId });
		return false;
	}

	let parsed: unknown;
	try {
		parsed = YAML.parse(content);
	} catch (error) {
		logger.warn("Failed to parse moment.yml", { documentId, error });
		return true;
	}

	const validated = DocumentMetadataSchema.safeParse(parsed);
	if (!validated.success) {
		logger.warn("Invalid moment.yml metadata", { documentId, error: validated.error });
		return true;
	}
	const nextMetadata = validated.data;

	if (isEqual(currentMetadata, nextMetadata)) {
		logger.debug("External metadata unchanged, skipping", { documentId });
		return true;
	}

	const previousPages = currentMetadata.pagesMetadata ?? [];
	const nextPages = nextMetadata.pagesMetadata ?? [];

	reconcileMetadataPages(documentId, previousPages, nextPages, set);

	// Detect new pages (present in nextPages but not in previousPages)
	const previousPageIds = new Set(previousPages.map((p) => p.id));
	const newPages = nextPages.filter((p) => !previousPageIds.has(p.id));

	// Check if the document has an active collab session
	const document = get($document(documentId));
	const remoteEntry = document ? docsListItemRemoteEntry(document) : undefined;
	const hasCollab = !!docsListCollabEntry(remoteEntry);

	if (newPages.length > 0 && hasCollab) {
		// For collab documents with new pages, we must NOT apply the full metadata
		// locally yet. The $documentLoaderEffect would see the new pages and try to
		// load their editor states from Reboot, but the PageAuthority hasn't been
		// created yet (StateNotConstructed error).
		//
		// Instead:
		//   1. Apply metadata locally WITHOUT the new pages (so other metadata
		//      changes like title, reorders of existing pages, etc. still take
		//      effect immediately).
		//   2. Fire $syncNewPagesToCollab which calls registry.createNewPage() on
		//      the Reboot server. This atomically creates the PageAuthority AND
		//      updates the metadata on the server.
		//   3. The full metadata (with the new pages) will arrive via the
		//      remoteChanges stream, which triggers $documentLoaderEffect to safely
		//      load the new pages from Reboot.
		//
		// Compute the pageIndex for each new page based on its position in the
		// nextPages array (the order from moment.yml).
		const newPagesWithIndex = newPages.map((page) => ({
			...page,
			pageIndex: nextPages.findIndex((p) => p.id === page.id),
		}));

		// Apply metadata without new pages so other changes are visible immediately
		const metadataWithoutNewPages = {
			...nextMetadata,
			pagesMetadata: nextPages.filter((p) => previousPageIds.has(p.id)),
		};

		logger.info("Applying external metadata change (excluding new pages for collab)", {
			documentId,
			newPageIds: newPages.map((p) => p.id),
		});
		set($applyExternalDocumentMetadata, {
			key: metadataKey,
			metadata: metadataWithoutNewPages,
		});

		logger.info("Syncing new pages to collab", {
			documentId,
			newPageIds: newPagesWithIndex.map((p) => p.id),
			newPageIndices: newPagesWithIndex.map((p) => p.pageIndex),
		});
		set($syncNewPagesToCollab, {
			documentId,
			newPages: newPagesWithIndex,
			metadataKey,
			fullMetadata: nextMetadata,
		});
	} else {
		// For local-only documents or when there are no new pages, apply the full
		// metadata immediately. The $documentLoaderEffect will load new pages from
		// disk (no Reboot dependency).
		logger.info("Applying external metadata change", { documentId });
		set($applyExternalDocumentMetadata, { key: metadataKey, metadata: nextMetadata });
	}

	return true;
};

const handleRequestPageChange = (
	path: string,
	content: string,
	get: Getter,
	set: Setter
): boolean => {
	const documentId = getDocumentIdFromPath(path);
	if (!documentId) {
		logger.debug("Could not extract documentId from path", { path });
		return true;
	}

	if (get($isCloning(documentId))) {
		logger.debug("Document is being cloned, dropping request page change", { documentId });
		return true;
	}

	if (get($isRenamingPage(documentId))) {
		logger.debug("Page directory rename in progress, dropping request page change", {
			documentId,
		});
		return true;
	}

	const localPathLoadable = get(loadable($documentLocalPath(documentId)));
	if (localPathLoadable.state === "loading") {
		logger.debug("Document local path loading", { documentId });
		return false;
	}
	if (localPathLoadable.state === "hasError") {
		logger.debug("Document local path error, will retry", {
			documentId,
			error: localPathLoadable.error,
		});
		return false;
	}
	if (!localPathLoadable.data) {
		logger.debug("Document local path undefined, will retry", { documentId });
		return false;
	}
	const documentRootPath = localPathLoadable.data;

	const metadataKey = metadataKeyForDocsRoute(documentId);
	const metadata = get($documentMetadata(metadataKey));
	if (!metadata) {
		logger.debug("Document metadata not loaded, will retry", { documentId });
		return false;
	}

	const resolved = resolveFilePathToPage(
		path,
		documentId,
		documentRootPath,
		metadata.pagesMetadata
	);
	if (!resolved) {
		logger.debug("Could not resolve request page path, dropping", { path, documentRootPath });
		return true;
	}

	const pageMetadata = metadata.pagesMetadata.find((page) => page.id === resolved.pageId);
	if (!pageMetadata || normalizePageType(pageMetadata.type) !== "request") {
		logger.debug("Resolved page is not a request page", {
			documentId,
			pageId: resolved.pageId,
		});
		return true;
	}

	let parsed: unknown;
	try {
		parsed = YAML.parse(content);
	} catch (error) {
		logger.warn("Failed to parse request page yaml", { documentId, error });
		return true;
	}

	const validated = HttpRequestPageSchema.safeParse(parsed);
	if (!validated.success) {
		logger.warn("Invalid request page yaml", {
			documentId,
			pageId: resolved.pageId,
			error: validated.error,
		});
		return true;
	}

	const requestKey = requestPageKeyForDocsRoute({
		documentId,
		pageId: resolved.pageId,
	});
	const currentRecord = get($httpRequestPage(requestKey));
	if (!currentRecord) {
		logger.debug("Request page not loaded, dropping", {
			documentId,
			pageId: resolved.pageId,
		});
		return true;
	}

	if (isEqual(currentRecord.request, validated.data)) {
		logger.debug("External request page unchanged, skipping", {
			documentId,
			pageId: resolved.pageId,
		});
		return true;
	}

	logger.info("Applying external request page change", {
		documentId,
		pageId: resolved.pageId,
	});
	set($applyExternalHttpRequestPage, { key: requestKey, request: validated.data });

	return true;
};

export const $fileWatcherEffect = atomEffect((get, set) => {
	const fileChanges = get($fileChanges);

	// Track paths that were successfully processed so we can remove them after
	// the loop to avoid memory leaks from accumulating file contents.
	const processedPaths: string[] = [];

	for (const [path, content] of Object.entries(fileChanges)) {
		logger.debug("Processing file change", { path, contentLength: content.length });

		let processed = false;
		if (isMomentYamlPath(path)) {
			processed = handleMomentYamlChange(path, content, get, set);
		} else if (path.endsWith(".md")) {
			processed = handleMarkdownChange(path, content, get, set);
		} else if (isYamlPath(path)) {
			processed = handleRequestPageChange(path, content, get, set);
		} else {
			processed = true;
		}

		if (processed) {
			processedPaths.push(path);
		}
	}

	// Remove processed entries to prevent memory leak from accumulating file contents.
	if (processedPaths.length > 0) {
		set($fileChanges, (prev) => {
			const next = { ...prev };
			for (const path of processedPaths) {
				delete next[path];
			}
			return next;
		});
	}
});
$fileWatcherEffect.debugLabel = "fileWatcherEffect";
